import React, { useState, useEffect } from "react";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import {
  SelectDropdown,
  SearchableSelectDropdown,
  StandardSearchDropdown,
  FilterBar,
  ClearAllButton,
} from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";

const COLLAB_LEVELS = [
  { value: null, label: "All Levels" },
  { value: 1, label: "Level 1: Needs Intervention" },
  { value: 2, label: "Level 2: Needs Guidance" },
  { value: 3, label: "Level 3: Effective Collaboration" },
  { value: 4, label: "Level 4: Exceed Expectations" },
];

const DATE_RANGES = [
  { value: "last_4_weeks", label: "Last 4 weeks" },
  { value: "last_8_weeks", label: "Last 8 weeks" },
  { value: "this_school_year", label: "This School Year" },
  { value: "all_time", label: "All Time" },
];

export const SessionHistoryFilters = ({
  classes,
  filters,
  onFilterChange,
}) => {
  const [students, setStudents] = useState([]);
  const [standardDisplayLabel, setStandardDisplayLabel] = useState(
    filters.standardDisplayLabel || filters.standardCode || null
  );

  // Fetch students when classroomId changes
  useEffect(() => {
    let cancelled = false;
    const fetchStudents = async () => {
      try {
        const params = new URLSearchParams();
        if (filters.classroomId != null) params.set("classroom_id", filters.classroomId);
        if (filters.educatorId) params.set("educator_id", filters.educatorId);
        const queryStr = params.toString();
        const url = `account/session_history/students${queryStr ? `?${queryStr}` : ""}`;
        const response = await request(url, { method: "GET" });
        if (!cancelled) setStudents(response || []);
      } catch (e) {
        if (!cancelled) {
          logError("Failed to fetch students", e, {
            category: LogCategory.ERROR,
          });
          setStudents([]);
        }
      }
    };
    fetchStudents();
    return () => { cancelled = true; };
  }, [filters.classroomId, filters.educatorId]);

  const classOptions = [
    { value: null, label: "All Classes", pinned: true },
    ...(classes || []).map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year })),
  ];

  const studentOptions = [
    { value: null, label: "All Students" },
    ...students.map((s) => ({
      value: s.student_id,
      label: s.display_name || "Unknown Student",
    })),
  ];

  const selectedClassName =
    filters.classroomId == null
      ? "All Classes"
      : classDisplayName(classes?.find((c) => String(c.id) === String(filters.classroomId))) || "All Classes";

  const selectedStudentName =
    filters.studentId == null
      ? "All Students"
      : students.find((s) => String(s.student_id) === String(filters.studentId))?.display_name || "All Students";

  const selectedCollabLabel =
    COLLAB_LEVELS.find((l) => l.value === filters.collaborationLevel)?.label || "All Levels";

  const selectedDateLabel = (() => {
    if (!filters.startDate) {
      return DATE_RANGES.find((d) => d.value === filters.dateRange)?.label || "Last 4 weeks";
    }
    const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (filters.endDate) {
      const diff = Math.round((new Date(filters.endDate + "T00:00:00") - new Date(filters.startDate + "T00:00:00")) / 86400000);
      if (diff > 7) return `${fmt(filters.startDate)} – ${fmt(filters.endDate)}`;
    }
    return `Week of ${fmt(filters.startDate)}`;
  })();

  // A deep link arrives pinned to one session (a safety concern quote) or one
  // bundle (a lesson report's "See Session Report"), with no visible chip.
  // Any filter change releases the pin; otherwise the new filter stacks on
  // top of it and the list looks stuck on the same session or on nothing.
  const unpinned = { ...filters, sessionId: null, assignmentId: null };

  const handleClassChange = (classroomId) => {
    onFilterChange({
      ...unpinned,
      classroomId,
      // A specific class has its own roster, so a student remembered from
      // another class may not be in it. All Classes holds every student, so
      // widening to it keeps the one already chosen: clearing the class is
      // how a teacher steps out of a report's funnel one level at a time.
      studentId: classroomId ? null : filters.studentId,
    });
  };

  const handleStudentChange = (studentId) => {
    onFilterChange({ ...unpinned, studentId });
  };

  const handleStandardChange = (code, label) => {
    setStandardDisplayLabel(label);
    onFilterChange({ ...unpinned, standardCode: code, standardDisplayLabel: label });
  };

  const handleCollabChange = (collaborationLevel) => {
    onFilterChange({ ...unpinned, collaborationLevel });
  };

  const handleDateChange = (dateRange) => {
    const { startDate, endDate, ...rest } = unpinned;
    onFilterChange({ ...rest, dateRange });
  };

  const handleClearAll = () => {
    const { startDate, endDate, ...rest } = unpinned;
    onFilterChange({
      ...rest,
      classroomId: null,
      studentId: null,
      standardCode: null,
      standardDisplayLabel: null,
      collaborationLevel: null,
      dateRange: "all_time",
    });
    setStandardDisplayLabel(null);
  };

  return (
    <FilterBar>
      <SelectDropdown
        label="Class"
        value={filters.classroomId}
        displayText={selectedClassName}
        options={classOptions}
        groupBySchoolYear
        onChange={handleClassChange}
        clearable
      />
      <SearchableSelectDropdown
        label="Student"
        value={filters.studentId}
        displayText={selectedStudentName}
        options={studentOptions}
        onChange={handleStudentChange}
        clearable
      />
      <StandardSearchDropdown
        label="Domain/Standard"
        value={filters.standardCode}
        displayLabel={standardDisplayLabel}
        classroomId={filters.classroomId}
        studentId={filters.studentId}
        educatorId={filters.educatorId}
        dateRange={filters.dateRange}
        onChange={handleStandardChange}
      />
      <SelectDropdown
        label="Group Collaboration Level"
        value={filters.collaborationLevel}
        displayText={selectedCollabLabel}
        options={COLLAB_LEVELS}
        onChange={handleCollabChange}
        clearable
      />
      <SelectDropdown
        label="Date Range"
        value={filters.dateRange}
        displayText={selectedDateLabel}
        options={DATE_RANGES}
        onChange={handleDateChange}
        clearable
        clearValue="all_time"
      />
      <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>
    </FilterBar>
  );
};
